/**
 * uploadRecovery — pure decisions for "what do we do when interrupted?"
 *
 * Constitutional reference: PLATFORM_REBUILD_DOCTRINE.md §E.
 * Architecture reference:   PLATFORM_REBUILD_ARCHITECTURE.md §7.
 *
 * What this is:
 *   A pure module — no React, no side effects, no network — that
 *   answers three questions about an in-flight upload:
 *
 *     attemptResume(upload_id, fileHandle): probe the server, return
 *       { resumable, last_chunk_index, reason } telling the caller
 *       whether the upload can be picked up where it left off.
 *
 *     shouldAutoRetry(error): given a thrown error from a chunk PUT,
 *       return { retry, delay_ms, attempts_left } using the
 *       operator-locked retry policy (4 attempts, 1.5/3/6/12 s).
 *
 *     classifyTerminal(error): if shouldAutoRetry returns retry=false,
 *       classify the terminal reason so the UI can surface the right
 *       named state ("auth_expired" / "integrity_failed" / etc.).
 *
 * Phase A scope discipline:
 *   This module is created and tested in Phase A. It is NOT YET
 *   wired into chunkedUpload.js. Wiring is Phase B (post operator
 *   confirmation that the foundation behaves cleanly). Existing
 *   retry logic inside chunkedUpload.js is left untouched.
 */

import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const RETRY_DELAYS_MS = [1500, 3000, 6000, 12000];   // 4 attempts
const MAX_RETRY_ATTEMPTS = RETRY_DELAYS_MS.length;

// HTTP statuses we treat as transient (retryable). Operator-locked
// per PLATFORM_REBUILD_ARCHITECTURE.md §7 row "Chunk PUT 408/429/...".
const TRANSIENT_STATUSES = new Set([408, 429, 502, 503, 504, 520, 521, 522, 523, 524]);

// Statuses we NEVER auto-retry. 401 is terminal-for-this-session
// (the operator must re-login). 4xx other than 408/429 are
// client-side bugs that won't get better on retry.
const NON_RETRYABLE_STATUSES = new Set([400, 401, 403, 404, 409, 410, 413, 415, 422]);

/**
 * Probe the server for an existing chunked-upload session.
 * Returns a discriminated result the caller can act on without
 * making its own HTTP decisions.
 *
 *   { resumable: true,  last_chunk_index: N, total_chunks: M }   -> resume from N+1
 *   { resumable: false, reason: "session_expired" }              -> 410-equivalent
 *   { resumable: false, reason: "session_unknown" }              -> 404
 *   { resumable: false, reason: "network_unreachable" }          -> raise to caller
 *
 * NEVER throws on a routine "session not found" answer. Throws only
 * if the caller passed a malformed input or the network is genuinely
 * unreachable.
 */
export async function attemptResume({ upload_id, signal } = {}) {
  if (!upload_id || typeof upload_id !== "string") {
    throw new Error("attemptResume requires a non-empty upload_id");
  }
  try {
    const res = await axios.get(
      `${API}/api/uploads/chunked/${encodeURIComponent(upload_id)}/status`,
      { withCredentials: true, signal },
    );
    const data = res.data || {};
    if (data.status === "complete") {
      return { resumable: false, reason: "already_complete", complete: true };
    }
    if (data.status === "expired") {
      return { resumable: false, reason: "session_expired" };
    }
    const received = Array.isArray(data.received_chunks) ? data.received_chunks : [];
    const lastIndex = received.length > 0 ? Math.max(...received) : -1;
    return {
      resumable: true,
      last_chunk_index: lastIndex,
      next_chunk_index: lastIndex + 1,
      total_chunks: data.total_chunks ?? null,
      bytes_received: data.bytes_received ?? null,
      total_bytes: data.total_bytes ?? null,
      raw_status: data.status,
    };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) {
      return { resumable: false, reason: "session_unknown" };
    }
    if (status === 410) {
      return { resumable: false, reason: "session_expired" };
    }
    if (status === 401) {
      return { resumable: false, reason: "auth_expired" };
    }
    // Detect Cloudflare-shaped 5xx (empty body, no `detail`) so the
    // operator-visible diagnostic surface can distinguish "edge
    // ate the request" from "real backend error". Pure observability
    // — does not change the retry/recovery decision.
    const body = err?.response?.data;
    const cloudflare_shaped = (
      typeof status === "number"
      && status >= 500 && status < 600
      && (body == null
          || (typeof body === "string" && body.trim() === "")
          || (typeof body === "object" && !body.detail))
    );
    // Network unreachable, CORS, etc. The caller may want to retry
    // the PROBE itself with backoff — that's a different concern from
    // the upload retry policy below.
    return {
      resumable: false,
      reason: "network_unreachable",
      error: String(err?.message || err),
      http_status: status ?? null,
      cloudflare_shaped,
    };
  }
}

/**
 * Decide whether a per-chunk PUT failure should be auto-retried.
 * The caller passes the error AND the attempt count so far (0-based).
 *
 *   shouldAutoRetry(err, 0) -> { retry: true, delay_ms: 1500, attempts_left: 3 }
 *   shouldAutoRetry(err, 3) -> { retry: false, delay_ms: 0, attempts_left: 0, reason: "attempts_exhausted" }
 *   shouldAutoRetry(authErr) -> { retry: false, delay_ms: 0, attempts_left: ..., reason: "auth_expired" }
 */
export function shouldAutoRetry(error, attempts_so_far = 0) {
  const status = error?.response?.status;

  // Auth — never retry. Caller must surface "session expired".
  if (status === 401) {
    return { retry: false, delay_ms: 0, attempts_left: 0, reason: "auth_expired" };
  }
  // Hard client errors — never retry. Caller surfaces the failure.
  if (status && NON_RETRYABLE_STATUSES.has(status)) {
    return { retry: false, delay_ms: 0, attempts_left: 0, reason: `non_retryable_${status}` };
  }
  // Out of budget.
  if (attempts_so_far >= MAX_RETRY_ATTEMPTS) {
    return { retry: false, delay_ms: 0, attempts_left: 0, reason: "attempts_exhausted" };
  }
  // Transient status — retry.
  if (status && TRANSIENT_STATUSES.has(status)) {
    return {
      retry: true,
      delay_ms: RETRY_DELAYS_MS[attempts_so_far],
      attempts_left: MAX_RETRY_ATTEMPTS - attempts_so_far - 1,
      reason: `transient_${status}`,
    };
  }
  // Network error (no response) — retry.
  if (!status && (error?.code === "ERR_NETWORK" || error?.message?.toLowerCase()?.includes("network"))) {
    return {
      retry: true,
      delay_ms: RETRY_DELAYS_MS[attempts_so_far],
      attempts_left: MAX_RETRY_ATTEMPTS - attempts_so_far - 1,
      reason: "network_error",
    };
  }
  // Anything else: do not silently retry. Operator surfaces it.
  return { retry: false, delay_ms: 0, attempts_left: 0, reason: "unknown_terminal" };
}

/**
 * Classify a terminal (non-retryable) error so the UI can pick the
 * right named state. Operator-locked per architecture §7.
 *
 *   classifyTerminal(authErr)      -> "auth_expired"
 *   classifyTerminal(integrityErr) -> "integrity_failed"
 *   classifyTerminal(otherErr)     -> "fatal"
 *   classifyTerminal(exhaustedErr) -> "transient_exhausted"
 */
export function classifyTerminal(error, { attempts_exhausted = false } = {}) {
  const status = error?.response?.status;
  if (status === 401) return "auth_expired";
  if (status === 410) return "session_expired";
  if (status === 409 || status === 422) return "integrity_failed";
  if (attempts_exhausted) return "transient_exhausted";
  if (status >= 400 && status < 500) return "fatal";   // client bug
  if (status >= 500) return "fatal_server";
  return "fatal_unknown";
}

// Exposed constants for the lifecycle drawer and audit log to read
// without re-deriving — single source of truth.
export const RETRY_POLICY = Object.freeze({
  delays_ms: [...RETRY_DELAYS_MS],
  max_attempts: MAX_RETRY_ATTEMPTS,
  transient_statuses: [...TRANSIENT_STATUSES],
  non_retryable_statuses: [...NON_RETRYABLE_STATUSES],
});
